// StateSelector.tsx
import React, { useState, useEffect } from "react"

import TailwindSelector from "./Selector"

export interface StateSelectorProps {
  /**
   * The currently selected state (optional)
   */
  selectedState?: string

  /**
   * Label to display above the selector (optional)
   */
  label?: string

  /**
   * CSS class name for the label (optional)
   */
  labelClassName?: string

  /**
   * Callback fired when state selection changes
   */
  onStateChange: (stateCode: string, stateName: string) => void

  /**
   * Whether the selector is disabled (optional)
   */
  disabled?: boolean

  /**
   * Placeholder text when no state is selected (optional)
   */
  placeholder?: string
}

const StateSelector: React.FC<StateSelectorProps> = ({
  selectedState,
  label = `State`,
  labelClassName,
  onStateChange,
  disabled = false,
  placeholder = `Select a state`,
}) => {
  // US states array with ID (state code) and label (state name)
  const stateOptions = [
    { id: `AL`, label: `Alabama` },
    { id: `AK`, label: `Alaska` },
    { id: `AZ`, label: `Arizona` },
    { id: `AR`, label: `Arkansas` },
    { id: `CA`, label: `California` },
    { id: `CO`, label: `Colorado` },
    { id: `CT`, label: `Connecticut` },
    { id: `DE`, label: `Delaware` },
    { id: `FL`, label: `Florida` },
    { id: `GA`, label: `Georgia` },
    { id: `HI`, label: `Hawaii` },
    { id: `ID`, label: `Idaho` },
    { id: `IL`, label: `Illinois` },
    { id: `IN`, label: `Indiana` },
    { id: `IA`, label: `Iowa` },
    { id: `KS`, label: `Kansas` },
    { id: `KY`, label: `Kentucky` },
    { id: `LA`, label: `Louisiana` },
    { id: `ME`, label: `Maine` },
    { id: `MD`, label: `Maryland` },
    { id: `MA`, label: `Massachusetts` },
    { id: `MI`, label: `Michigan` },
    { id: `MN`, label: `Minnesota` },
    { id: `MS`, label: `Mississippi` },
    { id: `MO`, label: `Missouri` },
    { id: `MT`, label: `Montana` },
    { id: `NE`, label: `Nebraska` },
    { id: `NV`, label: `Nevada` },
    { id: `NH`, label: `New Hampshire` },
    { id: `NJ`, label: `New Jersey` },
    { id: `NM`, label: `New Mexico` },
    { id: `NY`, label: `New York` },
    { id: `NC`, label: `North Carolina` },
    { id: `ND`, label: `North Dakota` },
    { id: `OH`, label: `Ohio` },
    { id: `OK`, label: `Oklahoma` },
    { id: `OR`, label: `Oregon` },
    { id: `PA`, label: `Pennsylvania` },
    { id: `RI`, label: `Rhode Island` },
    { id: `SC`, label: `South Carolina` },
    { id: `SD`, label: `South Dakota` },
    { id: `TN`, label: `Tennessee` },
    { id: `TX`, label: `Texas` },
    { id: `UT`, label: `Utah` },
    { id: `VT`, label: `Vermont` },
    { id: `VA`, label: `Virginia` },
    { id: `WA`, label: `Washington` },
    { id: `WV`, label: `West Virginia` },
    { id: `WI`, label: `Wisconsin` },
    { id: `WY`, label: `Wyoming` },
    { id: `DC`, label: `District of Columbia` },
  ]

  // Find the selected state object based on state code
  const selected = selectedState
    ? stateOptions.find((state) => state.id === selectedState)
    : null

  const handleSelectChange = (stateCode: string) => {
    const state = stateOptions.find((state) => state.id === stateCode)
    if (state) {
      onStateChange(state.id, state.label)
    }
  }

  return (
    <TailwindSelector
      label={label}
      labelClassName={labelClassName}
      options={stateOptions}
      selected={selected}
      onSelectChange={handleSelectChange}
    />
  )
}

export default StateSelector
